import { defineComponent, h, render, Fragment } from 'vue'

let idCounter = 0

const POSITIONS = [
  'top-right',
  'top-left',
  'top-center',
  'bottom-right',
  'bottom-left',
  'bottom-center'
]

/* =========================
   Staggered Component
========================= */

const StaggeredToast = defineComponent({
  props: { messages: Array },
  data() {
    return { current: '' }
  },
  mounted() {
    if (!this.messages?.length) return

    this.current = this.messages[0].text
    let elapsed = 0

    this.messages.slice(1).forEach(m => {
      elapsed += m.delay || 0
      setTimeout(() => {
        this.current = m.text
      }, elapsed)
    })
  },
  render() {
    return h(
      'transition',
      { name: 'toasted-fade', mode: 'out-in' },
      () => [h('span', { key: this.current }, this.current)]
    )
  }
})

/* =========================
   Toast Container
========================= */

// Plain state rendered with Vue's synchronous `render()` after every change,
// not a reactive component. A component commits to the DOM on the next
// microtask, but callers use the handle's `el` the moment `show()` returns
// (admixCalc wires a click listener onto its "add Raw DNA files" bubble that
// way), so a toast's node has to exist before `show()` returns.
const state = {
  root: null,   // the div under <body> that holds every .toasted-container
  toasts: {}    // position -> toasts shown there, oldest first
}
POSITIONS.forEach(p => state.toasts[p] = [])

function bucket(position) {
  // Custom positions such as 'bottom-center-upLittle' get a bucket on demand
  if (!state.toasts[position]) {
    state.toasts[position] = []
  }
  return state.toasts[position]
}

function addToast(toast) {
  const list = bucket(toast.position)

  if (toast.singleton) {
    list.forEach(t => clearTimeout(t._timer))
    list.length = 0
  }

  list.push(toast)

  if (toast.duration > 0) {
    toast._timer = setTimeout(() => {
      removeToast(toast)
    }, toast.duration)
  }

  renderContainer()
}

function removeToast(toast) {
  clearTimeout(toast._timer)
  const list = bucket(toast.position)
  const index = list.indexOf(toast)
  if (index >= 0) list.splice(index, 1)
  renderContainer()
}

function clearToasts() {
  Object.keys(state.toasts).forEach(p => {
    state.toasts[p].forEach(t => clearTimeout(t._timer))
    state.toasts[p] = []
  })
  renderContainer()
}

/**
 * Swipe/drag-to-dismiss — works with both touch (mobile) and mouse (desktop).
 * vue-toasted enabled this by default (closeOnSwipe: true).
 */
function onPointerDown(toast, e) {
  if (toast.closeOnSwipe === false) return
  toast._startX = e.clientX
  toast._startY = e.clientY
  toast._swiping = false
  toast._swipeDx = 0
  toast._el = e.currentTarget
  toast._pointerId = e.pointerId
}

function onPointerMove(toast, e) {
  if (toast._startX == null || toast.closeOnSwipe === false) return
  const dx = e.clientX - toast._startX
  const dy = e.clientY - toast._startY
  if (Math.abs(dx) > Math.abs(dy) && Math.abs(dx) > 10) {
    // Capture pointer only once swiping begins, so clicks on
    // action buttons are not intercepted
    if (!toast._swiping && toast._pointerId != null && toast._el) {
      toast._el.setPointerCapture(toast._pointerId)
    }
    toast._swiping = true
    toast._swipeDx = dx
    if (toast._el) {
      toast._el.style.transform = `translateX(${dx}px)`
      toast._el.style.opacity = Math.max(0, 1 - Math.abs(dx) / 200)
    }
  }
}

function onPointerUp(toast, e) {
  if (toast._startX == null) return
  if (toast._swiping && Math.abs(toast._swipeDx || 0) > 80) {
    // Animate out then remove
    if (toast._el) {
      const dir = toast._swipeDx > 0 ? 1 : -1
      toast._el.style.transition = 'transform 0.2s ease, opacity 0.2s ease'
      toast._el.style.transform = `translateX(${dir * 300}px)`
      toast._el.style.opacity = '0'
    }
    setTimeout(() => removeToast(toast), 200)
  } else if (toast._el) {
    toast._el.style.transform = ''
    toast._el.style.opacity = ''
  }
  toast._startX = null
  toast._swiping = false
  toast._swipeDx = 0
  toast._pointerId = null
}

function renderContainer() {
  if (!state.root) return

  // Render one .toasted-container per position (matches vue-toasted DOM)
  const containers = Object.keys(state.toasts)
    .filter(position => state.toasts[position].length > 0)
    .map(position =>
      h(
        'div',
        {
          key: position,
          class: [
            'toasted-container',
            position,
            // Apply containerClass from every toast there (they share position)
            ...state.toasts[position]
              .filter(t => t.containerClass)
              .map(t => t.containerClass)
          ]
        },
        state.toasts[position].map(toast =>
          h(
            'div',
            {
              key: toast.id,
              'data-toast-id': toast.id,
              class: [
                'toasted',
                toast.theme,
                toast.type,
                toast.className,
              ],
              onPointerdown: (e) => onPointerDown(toast, e),
              onPointermove: (e) => onPointerMove(toast, e),
              onPointerup: (e) => onPointerUp(toast, e),
            },
            [
              toast.render(),
              toast.renderActions?.(),
              toast.renderClose?.()
            ]
          )
        )
      )
    )

  render(h(Fragment, containers), state.root)
}

/* =========================
   Base styles (replaces vue-toasted CSS)
========================= */

const BASE_CSS = `
.toasted-container {
  position: fixed;
  z-index: 99999;
  pointer-events: none;
  display: flex;
  flex-direction: column;
  align-items: center;
}
.toasted-container > .toasted {
  pointer-events: auto;
  margin-bottom: 8px;
  transition: transform 0.2s ease, opacity 0.2s ease;
}
.toasted-container .toasted {
  top: 0;
  position: relative;
  max-width: 100%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 38px;
  padding: 6px 20px;
  border-radius: 2px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.24);
  font-size: 15px;
  font-weight: 300;
  cursor: pointer;
  word-break: break-word;
  animation: toasted-fadein 0.3s;
}
.toasted.toasted-primary {
  background: #353535;
  color: #fff;
  border-radius: 3px;
}
.toasted.toasted-primary .action {
  color: #a1c2fa;
}
.toasted-container .toasted .action {
  text-decoration: none;
  font-size: 0.85em;
  padding: 5px 8px;
  margin-left: 8px;
  border-radius: 3px;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  font-weight: 600;
  cursor: pointer;
}
.toasted-container .toasted .action:hover {
  text-decoration: underline;
}
.toasted-container .toasted .action.close-icon {
  font-size: 1.2em;
  margin-left: 12px;
}
.toasted-container.top-right {
  top: 10%;
  right: 7%;
}
.toasted-container.top-left {
  top: 10%;
  left: 7%;
}
.toasted-container.top-center {
  top: 10%;
  left: 50%;
  transform: translateX(-50%);
  text-align: center;
}
.toasted-container.bottom-right {
  bottom: 7%;
  right: 5%;
}
.toasted-container.bottom-left {
  bottom: 7%;
  left: 5%;
}
.toasted-container.bottom-center {
  bottom: 7%;
  left: 50%;
  transform: translateX(-50%);
  text-align: center;
}
.toasted-container .toasted.bubble {
  background: #fff;
  color: #333;
  border-radius: 30px;
  padding: 4px 18px;
}
@keyframes toasted-fadein {
  from { opacity: 0; transform: translateY(-10px); }
  to   { opacity: 1; transform: translateY(0); }
}
`

let styleInjected = false
function injectBaseStyles() {
  if (styleInjected) return
  styleInjected = true
  const style = document.createElement('style')
  style.setAttribute('data-toasted', '')
  style.textContent = BASE_CSS
  document.head.appendChild(style)
}

/* =========================
   Mount container once
========================= */

function mountContainer() {
  if (state.root) return
  injectBaseStyles()
  state.root = document.createElement('div')
  document.body.appendChild(state.root)
}

/**
 * vue-toasted kept a toast up until it was dismissed for any falsy duration
 * (it read `options.duration || null`), and callers still lean on that with
 * `duration: null` — admixCalc's "add Raw DNA files" bubble, Nav's pushed
 * notifications. Only an omitted duration takes this plugin's 5s default.
 */
function parseDuration(duration) {
  if (duration === undefined) return 5000
  return Number(duration) || 0
}

/* =========================
   Plugin
========================= */

const ToastPlugin = {
  install(app) {
    mountContainer()

    const registry = {}

    function createToast(message, options = {}) {
      const id = ++idCounter

      // vue-toasted defaults to 'toasted-primary' theme when no theme specified
      const theme = options.theme || 'toasted-primary'

      const toast = {
        id,
        duration: parseDuration(options.duration),
        position: options.position || 'top-right',
        singleton: options.singleton,
        type: options.type || 'default',
        theme,
        className: options.className,
        containerClass: options.containerClass,
        // vue-toasted defaults closeOnSwipe to true
        closeOnSwipe: options.closeOnSwipe !== false,
        _timer: null,
        _el: null,

        render() {
          /* 1. Vue Component Support */
          if (options.component) {
            return h(options.component, options.props || {})
          }

          /* 2. Staggered Array */
          if (Array.isArray(message)) {
            return h(StaggeredToast, { messages: message })
          }

          /* 3. Object message */
          if (typeof message === 'object' && message !== null && message.text) {
            return h('span', { innerHTML: message.text })
          }

          /* 4. String message — always innerHTML (vue-toasted default) */
          return h('span', { innerHTML: message || '' })
        },

        renderActions() {
          if (!options.action) return null

          const actions = Array.isArray(options.action)
            ? options.action
            : [options.action]

          return actions.map(a =>
            h(
              'a',
              {
                class: 'action ripple',
                href: 'javascript:void(0)',
                onClick: (e) => {
                  // vue-toasted passes (event, toastObject) to onClick
                  if (a.onClick) a.onClick(e, handle)
                  if (a.dismiss !== false && !a.onClick) {
                    removeToast(toast)
                  }
                }
              },
              a.text || a.icon || ''
            )
          )
        },

        renderClose() {
          if (!options.closeIcon) return null
          return h(
            'a',
            {
              class: 'action close-icon',
              href: 'javascript:void(0)',
              onClick: () => removeToast(toast)
            },
            '\u00d7'
          )
        }
      }

      addToast(toast)

      // The handle returned to callers — matches vue-toasted API
      const handle = {
        // The toast's rendered `.toasted` node. It exists as soon as show()
        // returns (the container renders synchronously) and is kept after
        // the toast is dismissed, so a caller's removeEventListener on a
        // toast that has already gone is still safe.
        get el() {
          if (!toast._el && state.root) {
            toast._el = state.root.querySelector(`.toasted[data-toast-id="${id}"]`)
          }
          return toast._el
        },
        goAway(delay) {
          if (delay && delay > 0) {
            setTimeout(() => removeToast(toast), delay)
          } else {
            removeToast(toast)
          }
        },
        remove() {
          removeToast(toast)
        },
        text(newText) {
          message = newText
          renderContainer()
          return handle
        }
      }

      return handle
    }

    const toasted = {
      show: createToast,

      clear() {
        clearToasts()
      },

      success(message, opts = {}) {
        return createToast(message, { ...opts, type: 'success' })
      },

      error(message, opts = {}) {
        return createToast(message, { ...opts, type: 'error' })
      },

      info(message, opts = {}) {
        return createToast(message, { ...opts, type: 'info' })
      },

      register(name, fn, defaultOptions = {}) {
        registry[name] = (payload, opts = {}) =>
          createToast(fn(payload), {
            ...defaultOptions,
            ...opts
          })

        if (!toasted.global) toasted.global = {}
        toasted.global[name] = registry[name]
      },

      global: {}
    }

    app.config.globalProperties.$toasted = toasted
  }
}

export default ToastPlugin
